import { Application, Assets, Container, Graphics, Sprite, Texture, Ticker } from "pixi.js";
import type { ServerMessage } from "../shared/protocol.js";
import type { GameState } from "../shared/game-types.js";
import { GAME_ART_PATHS } from "./game-art.js";
import { GameScene } from "./game-scene.js";
import { LobbyView } from "./lobby.js";
import { GameConnection } from "./network.js";
import { COLORS, VIEW_HEIGHT, VIEW_WIDTH } from "./theme.js";

const PLAYER_NAME_KEY = "alvasAttackPlayerName";

function routeRoomCode(): string {
  const match = window.location.pathname.match(/^\/game\/([A-Za-z0-9]{5})\/?$/);
  return match?.[1]?.toUpperCase() || "";
}

function storedPlayerName(): string {
  return localStorage.getItem(PLAYER_NAME_KEY)?.trim().slice(0, 24) || "Skogsvän";
}

function rememberPlayerName(value: string): string {
  const name = value.trim().slice(0, 24) || "Skogsvän";
  localStorage.setItem(PLAYER_NAME_KEY, name);
  return name;
}

function playSound(path: string, volume = 0.35): void {
  const audio = new Audio(path);
  audio.volume = volume;
  void audio.play().catch(function () {
    // Browsers can block non-gesture audio; gameplay remains unaffected.
  });
}

async function start(): Promise<void> {
  const gameShellElement = document.getElementById("game-shell");
  const gameLoadingElement = document.getElementById("game-loading");
  if (!(gameShellElement instanceof HTMLElement) || !(gameLoadingElement instanceof HTMLElement)) {
    throw new Error("Game shell is missing from the page.");
  }
  const gameShell = gameShellElement;
  const gameLoading = gameLoadingElement;
  let app: Application | null = null;
  let sceneLayer: Container | null = null;
  let rendererPromise: Promise<void> | null = null;
  let resizeHandler: (() => void) | null = null;
  let gameScene: GameScene | null = null;
  let enteringRoomCode = "";
  let connectionStatus: "connecting" | "connected" | "disconnected" = "connecting";
  let autoJoinCode = routeRoomCode();
  let connection: GameConnection;
  let shuttingDown = false;
  let pendingSnapshot: GameState | null = null;
  let latestAppliedSnapshot: GameState | null = null;
  let diagnosticWindowStartedAt = performance.now();
  let diagnosticLastFrameAt = performance.now();
  let diagnosticLastSnapshotAt = 0;
  let diagnosticFrames = 0;
  let diagnosticLongFrames = 0;
  let diagnosticMaxFrameGapMs = 0;
  let diagnosticSnapshotsReceived = 0;
  let diagnosticSnapshotsApplied = 0;
  let diagnosticSnapshotsReplaced = 0;
  let diagnosticMaxSnapshotGapMs = 0;
  let diagnosticRenderErrors = 0;
  let diagnosticLastRenderError = "";
  let diagnosticLastConsoleErrorAt = 0;
  let diagnosticLastTickerCallbackAt = performance.now();
  let diagnosticTickerRecoveries = 0;
  let diagnosticClientBuildId = "unknown";
  let buildCheckInFlight = false;
  let buildReloadRequested = false;

  function describeError(error: unknown): string {
    if (error instanceof Error) {
      return error.name + ": " + error.message + (error.stack ? "\n" + error.stack : "");
    }
    return String(error);
  }

  function recordRenderingError(scope: string, error: unknown): void {
    diagnosticRenderErrors += 1;
    diagnosticLastRenderError = scope + ": " + describeError(error);
    try {
      gameScene?.recoverTransientAnimations();
    } catch (recoveryError) {
      diagnosticLastRenderError += "\nrecovery: " + describeError(recoveryError);
    }
    const now = performance.now();
    if (now - diagnosticLastConsoleErrorAt >= 1_000) {
      diagnosticLastConsoleErrorAt = now;
      console.error("Game rendering recovered from a " + scope + " error:", error);
    }
  }

  const handleWindowError = function (event: ErrorEvent): void {
    recordRenderingError("window", event.error ?? event.message);
  };
  const handleUnhandledRejection = function (event: PromiseRejectionEvent): void {
    recordRenderingError("unhandled-promise", event.reason);
  };
  window.addEventListener("error", handleWindowError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);

  function prepareGameRenderer(): Promise<void> {
    if (rendererPromise) {
      return rendererPromise;
    }
    gameShell.hidden = false;
    delete gameShell.dataset.ready;
    gameLoading.hidden = false;
    rendererPromise = (async function () {
      const nextApp = new Application();
      await nextApp.init({
        resizeTo: window,
        antialias: true,
        autoDensity: true,
        autoStart: false,
        backgroundColor: COLORS.ink,
        preference: "webgl",
        powerPreference: "high-performance",
        resolution: Math.min(window.devicePixelRatio || 1, 2),
      });
      nextApp.canvas.id = "game-canvas";
      nextApp.canvas.setAttribute("aria-label", "Alvas Attack – slagfält");
      gameShell.appendChild(nextApp.canvas);
      // Pixi normally renders in a separate low-priority ticker listener. An
      // exception in that listener prevents Pixi from scheduling another frame.
      // Rendering inside our protected listener below keeps the loop recoverable.
      nextApp.ticker.remove(nextApp.render, nextApp);

      const nextRoot = new Container();
      nextApp.stage.addChild(nextRoot);
      let backgroundTexture = Texture.WHITE;
      await Promise.all([
        Assets.load("/assets/backgrounds/forest-lanes-daylight.png")
          .then(function (texture) { backgroundTexture = texture as Texture; })
          .catch(function () {
            // The colored fallback underneath keeps the game playable without the asset.
          }),
        ...GAME_ART_PATHS.map(function (path) {
          return Assets.load(path).catch(function () {
            // GameScene keeps simple vector fallbacks for a failed individual asset.
          });
        }),
      ]);
      if (shuttingDown) {
        nextApp.destroy(true);
        return;
      }

      const background = new Sprite(backgroundTexture);
      background.width = VIEW_WIDTH;
      background.height = VIEW_HEIGHT;
      background.tint = backgroundTexture === Texture.WHITE ? 0x223c29 : 0xffffff;
      nextRoot.addChild(background);
      const backgroundShade = new Graphics()
        .rect(0, 0, VIEW_WIDTH, VIEW_HEIGHT)
        .fill({ color: COLORS.ink, alpha: 0.1 });
      nextRoot.addChild(backgroundShade);

      const nextSceneLayer = new Container();
      nextRoot.addChild(nextSceneLayer);
      const resizeRoot = function (): void {
        const scale = Math.min(nextApp.screen.width / VIEW_WIDTH, nextApp.screen.height / VIEW_HEIGHT);
        nextRoot.scale.set(scale);
        nextRoot.position.set(
          Math.round((nextApp.screen.width - VIEW_WIDTH * scale) / 2),
          Math.round((nextApp.screen.height - VIEW_HEIGHT * scale) / 2),
        );
      };
      resizeRoot();
      resizeHandler = function () {
        window.requestAnimationFrame(resizeRoot);
      };
      window.addEventListener("resize", resizeHandler);

      app = nextApp;
      sceneLayer = nextSceneLayer;
      diagnosticLastFrameAt = performance.now();
      diagnosticLastTickerCallbackAt = performance.now();
      nextApp.ticker.add(handleTicker);
      nextApp.ticker.start();
    })().catch(function (error) {
      rendererPromise = null;
      gameShell.hidden = true;
      throw error;
    });
    return rendererPromise;
  }

  const lobby = new LobbyView({
    routeCode: autoJoinCode,
    initialName: storedPlayerName(),
    onCreate: function (enteredName) {
      const name = rememberPlayerName(enteredName);
      connection.setPlayerName(name);
      connection.createRoom();
      lobby.setStatus("Skapar match…");
      playSound("/assets/sfx/ui-confirm.mp3");
    },
    onCreateAi: function (enteredName) {
      const name = rememberPlayerName(enteredName);
      connection.setPlayerName(name);
      connection.createAiRoom();
      lobby.setStatus("Väcker Skogsväktaren…");
      playSound("/assets/sfx/ui-confirm.mp3");
    },
    onJoin: function (enteredName, code) {
      const name = rememberPlayerName(enteredName);
      connection.setPlayerName(name);
      connection.joinRoom(code);
      lobby.setStatus("Går med i " + code.toUpperCase() + "…");
      playSound("/assets/sfx/ui-confirm.mp3");
    },
  });
  document.getElementById("loading")?.remove();

  async function enterRoom(message: Extract<ServerMessage, { type: "room_joined" }>): Promise<void> {
    const roomPath = "/game/" + message.code;
    if (window.location.pathname !== roomPath) {
      window.history.replaceState({}, "", roomPath);
    }
    if (gameScene || enteringRoomCode) {
      return;
    }
    enteringRoomCode = message.code;
    lobby.setBusy(true);
    lobby.setStatus("Förbereder slagfältet…");
    try {
      await prepareGameRenderer();
      if (shuttingDown || !sceneLayer) {
        return;
      }
      gameScene = new GameScene(message.code, message.seat, {
        placeBuilding: function (lane, column, buildingType) {
          connection.placeBuilding(lane, column, buildingType);
          playSound("/assets/sfx/card-play.mp3", 0.28);
        },
        claimGold: function (buildingId) {
          connection.claimGold(buildingId);
          playSound("/assets/sfx/ui-confirm.mp3", 0.3);
        },
        buyUnit: function (buildingId, unitType) {
          connection.buyUnit(buildingId, unitType);
          playSound("/assets/sfx/attack-creature.mp3", 0.3);
        },
        setDenQueueMode: function (buildingId, enabled) {
          connection.setDenQueueMode(buildingId, enabled);
          playSound("/assets/sfx/ui-click.mp3", 0.28);
        },
        reorderUnitQueue: function (buildingId, fromIndex, toIndex) {
          connection.reorderUnitQueue(buildingId, fromIndex, toIndex);
          playSound("/assets/sfx/ui-click.mp3", 0.24);
        },
        removeQueuedUnit: function (buildingId, index) {
          connection.removeQueuedUnit(buildingId, index);
          playSound("/assets/sfx/ui-click.mp3", 0.28);
        },
        releaseNextQueuedUnit: function (buildingId) {
          connection.releaseNextQueuedUnit(buildingId);
          playSound("/assets/sfx/attack-creature.mp3", 0.3);
        },
        releaseUnitQueue: function (buildingId) {
          connection.releaseUnitQueue(buildingId);
          playSound("/assets/sfx/attack-creature.mp3", 0.34);
        },
        requestRestart: function () {
          connection.requestRestart();
          playSound("/assets/sfx/ui-confirm.mp3", 0.3);
        },
      });
      gameScene.setConnectionStatus(connectionStatus);
      sceneLayer.addChild(gameScene.container);
      lobby.hide();
      lobby.dispose();
      gameLoading.hidden = true;
      gameShell.dataset.ready = "true";
      document.body.dataset.view = "game";
    } catch (error) {
      enteringRoomCode = "";
      lobby.setBusy(false);
      lobby.setStatus("Slagfältet kunde inte laddas. Ladda om sidan och försök igen.", "error");
      console.error(error);
    }
  }

  connection = new GameConnection(storedPlayerName(), {
    onReady: function () {
      if (shuttingDown) {
        return;
      }
      if (!gameScene) {
        lobby.setConnected(true);
      }
      if (autoJoinCode) {
        const code = autoJoinCode;
        autoJoinCode = "";
        lobby.setBusy(true);
        lobby.setStatus("Går med i " + code + "…");
        connection.joinRoom(code);
      }
    },
    onStatus: function (status) {
      if (shuttingDown) {
        return;
      }
      connectionStatus = status;
      if (!gameScene) {
        lobby.setConnected(status === "connected");
        if (status === "connecting") {
          lobby.setStatus("Ansluter till servern…");
        } else if (status === "disconnected") {
          lobby.setBusy(false);
          lobby.setStatus("Anslutningen bröts. Försöker igen…", "error");
        }
      }
      gameScene?.setConnectionStatus(status);
    },
    onMessage: function (message) {
      if (shuttingDown) {
        return;
      }
      if (message.type === "room_joined") {
        void enterRoom(message);
      } else if (message.type === "snapshot") {
        const receivedAt = performance.now();
        if (diagnosticLastSnapshotAt > 0) {
          diagnosticMaxSnapshotGapMs = Math.max(
            diagnosticMaxSnapshotGapMs,
            receivedAt - diagnosticLastSnapshotAt,
          );
        }
        diagnosticLastSnapshotAt = receivedAt;
        diagnosticSnapshotsReceived += 1;
        if (pendingSnapshot) {
          diagnosticSnapshotsReplaced += 1;
        }
        // A snapshot supersedes every older snapshot. Applying only the newest
        // one per rendered frame prevents a slow client from building a queue.
        pendingSnapshot = message.state;
      } else if (message.type === "error") {
        if (gameScene) {
          gameScene.showToast(message.message);
        } else {
          lobby.setBusy(false);
          lobby.setStatus(message.message, "error");
        }
      }
    },
  });
  connection.connect();

  function handleTicker(ticker: Ticker): void {
    const activeApp = app;
    if (!activeApp) {
      return;
    }
    const frameAt = performance.now();
    const frameGapMs = frameAt - diagnosticLastFrameAt;
    diagnosticLastFrameAt = frameAt;
    diagnosticLastTickerCallbackAt = frameAt;
    diagnosticFrames += 1;
    diagnosticMaxFrameGapMs = Math.max(diagnosticMaxFrameGapMs, frameGapMs);
    if (frameGapMs >= 250) {
      diagnosticLongFrames += 1;
    }
    try {
      if (gameScene && pendingSnapshot) {
        const snapshot = pendingSnapshot;
        pendingSnapshot = null;
        gameScene.update(snapshot);
        latestAppliedSnapshot = snapshot;
        diagnosticSnapshotsApplied += 1;
      }
      gameScene?.tick(ticker.deltaMS);
    } catch (error) {
      recordRenderingError("update", error);
    }
    try {
      activeApp.render();
    } catch (error) {
      recordRenderingError("render", error);
    }
  }

  function recoverTickerIfStalled(): void {
    const activeApp = app;
    if (!activeApp || document.hidden || !document.hasFocus()) {
      return;
    }
    const now = performance.now();
    if (activeApp.ticker.started && now - diagnosticLastTickerCallbackAt < 1_500) {
      return;
    }
    diagnosticTickerRecoveries += 1;
    diagnosticLastTickerCallbackAt = now;
    activeApp.ticker.stop();
    activeApp.ticker.start();
  }

  async function checkForUpdatedClientBuild(): Promise<void> {
    if (!navigator.onLine
      || !connection.diagnosticsAreEnabled()
      || buildCheckInFlight
      || buildReloadRequested
      || shuttingDown) {
      return;
    }
    buildCheckInFlight = true;
    try {
      const response = await fetch("/build/game.js?dev-check=" + Date.now(), {
        method: "HEAD",
        cache: "no-store",
      });
      const lastModified = response.headers.get("last-modified") || "";
      const modifiedAt = Date.parse(lastModified);
      if (lastModified) {
        diagnosticClientBuildId = lastModified;
      }
      // HTTP dates have one-second precision. A bundle newer than the page is
      // one the currently running tab cannot have loaded.
      if (Number.isFinite(modifiedAt) && modifiedAt > performance.timeOrigin + 1_000) {
        buildReloadRequested = true;
        window.location.reload();
      }
    } catch (_error) {
      // The normal WebSocket status handles a temporarily unavailable dev server.
    } finally {
      buildCheckInFlight = false;
    }
  }

  const tickerWatchdogInterval = window.setInterval(recoverTickerIfStalled, 1_000);
  const buildVersionInterval = window.setInterval(function () {
    void checkForUpdatedClientBuild();
  }, 2_000);
  void checkForUpdatedClientBuild();
  const diagnosticInterval = window.setInterval(function () {
    if (!connection.diagnosticsAreEnabled()) {
      return;
    }
    const now = performance.now();
    const performanceWithMemory = performance as Performance & {
      memory?: { usedJSHeapSize: number };
    };
    const snapshot = latestAppliedSnapshot;
    const timeSinceLastSnapshotMs = diagnosticLastSnapshotAt > 0 ? now - diagnosticLastSnapshotAt : 0;
    const memoryFields = performanceWithMemory.memory
      ? { heapUsedMb: Math.round(performanceWithMemory.memory.usedJSHeapSize / 1_048_576 * 10) / 10 }
      : {};
    const gameFields = snapshot ? {
      roomCode: snapshot.code,
      phase: snapshot.phase,
      roundNumber: snapshot.roundNumber,
      gameNowMs: snapshot.nowMs,
      buildings: snapshot.buildings.length,
      units: snapshot.units.length,
      events: snapshot.events.length,
    } : {};
    connection.reportDiagnostic({
      clientTimeMs: Date.now(),
      pageUptimeMs: now,
      windowMs: Math.max(1, now - diagnosticWindowStartedAt),
      frames: diagnosticFrames,
      maxFrameGapMs: diagnosticMaxFrameGapMs,
      longFrameCount: diagnosticLongFrames,
      snapshotsReceived: diagnosticSnapshotsReceived,
      snapshotsApplied: diagnosticSnapshotsApplied,
      snapshotsReplaced: diagnosticSnapshotsReplaced,
      maxSnapshotGapMs: Math.max(diagnosticMaxSnapshotGapMs, timeSinceLastSnapshotMs),
      pendingSnapshot: Boolean(pendingSnapshot),
      visibility: document.visibilityState,
      devicePixelRatio: window.devicePixelRatio || 1,
      rendererResolution: app?.renderer.resolution ?? 0,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      tickerStarted: app?.ticker.started ?? false,
      tickerRecoveryCount: diagnosticTickerRecoveries,
      lastFrameAgoMs: Math.max(0, now - diagnosticLastTickerCallbackAt),
      documentFocused: document.hasFocus(),
      clientBuildId: diagnosticClientBuildId,
      renderErrorCount: diagnosticRenderErrors,
      ...(diagnosticLastRenderError ? { lastRenderError: diagnosticLastRenderError.slice(0, 1_000) } : {}),
      ...memoryFields,
      ...gameFields,
    });
    diagnosticWindowStartedAt = now;
    diagnosticFrames = 0;
    diagnosticLongFrames = 0;
    diagnosticMaxFrameGapMs = 0;
    diagnosticSnapshotsReceived = 0;
    diagnosticSnapshotsApplied = 0;
    diagnosticSnapshotsReplaced = 0;
    diagnosticMaxSnapshotGapMs = 0;
    diagnosticRenderErrors = 0;
    diagnosticLastRenderError = "";
    diagnosticTickerRecoveries = 0;
  }, 2_000);
  document.addEventListener("visibilitychange", function () {
    const activeApp = app;
    if (!activeApp) {
      return;
    }
    if (document.hidden) {
      activeApp.ticker.stop();
    } else {
      diagnosticLastTickerCallbackAt = performance.now();
      activeApp.ticker.start();
    }
  });
  window.addEventListener("focus", recoverTickerIfStalled);
  window.addEventListener("pageshow", recoverTickerIfStalled);
  window.addEventListener("beforeunload", function () {
    shuttingDown = true;
    window.clearInterval(diagnosticInterval);
    window.clearInterval(tickerWatchdogInterval);
    window.clearInterval(buildVersionInterval);
    window.removeEventListener("error", handleWindowError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    window.removeEventListener("focus", recoverTickerIfStalled);
    window.removeEventListener("pageshow", recoverTickerIfStalled);
    if (resizeHandler) {
      window.removeEventListener("resize", resizeHandler);
      resizeHandler = null;
    }
    pendingSnapshot = null;
    const scene = gameScene;
    gameScene = null;
    connection.close();
    lobby.dispose();
    scene?.dispose();
    app?.destroy(true);
    app = null;
  });
}

void start().catch(function (error: unknown) {
  console.error(error);
  const loading = document.getElementById("loading");
  if (loading) {
    loading.textContent = "Spelet kunde inte starta. Ladda om sidan och försök igen.";
    return;
  }
  const lobbyStatus = document.getElementById("lobby-status");
  const lobbyMessage = document.getElementById("lobby-status-message");
  if (lobbyStatus && lobbyMessage) {
    lobbyStatus.dataset.tone = "error";
    lobbyMessage.textContent = "Klienten kunde inte starta. Ladda om sidan och försök igen.";
  }
});
